<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  iButton,
  iButtonSizes,
  iModal,
  iSpinner,
  iTextInput,
  openModal,
} from '@inscribe/signal';
import RuleEngine from '@/utils/ruleEngine';
import CheckDelete from '@/V2/decision-engine/checks/CheckDelete.vue';
import DecisionEngineCard from '@/V2/decision-engine/DecisionEngineCard.vue';
import EmptyPage from '@/V2/components/EmptyPage';
import DecisionEngineItemDescription from '@/V2/decision-engine/DecisionEngineItemDescription.vue';

export default {
  components: {
    CheckDelete,
    DecisionEngineCard,
    EmptyPage,
    iButton,
    iModal,
    iSpinner,
    iTextInput,
    DecisionEngineItemDescription,
  },
  data() {
    return {
      showNewRuleModal: false,
      newRule: null,
      ruleError: null,
      mode: '',
      displayLink: 'https://help.inscribe.ai/en/articles/5677805-checks',
      editingCheck: null,
      checkToBeDeleted: null,
      newCheckName: '',
      iButtonSizes,
    };
  },
  computed: {
    ...mapGetters({
      isOrgAdmin: 'isOrgAdmin',
    }),
    checks() {
      return this.$store.state.config.checks.slice().reverse();
    },
    validCheckName() {
      return this.newCheckName.trim() !== '';
    },
    checksLoading() {
      return this.$store.state.config.checksLoading;
    },
    isValidRuleSet() {
      return (
        this.rulesSchema &&
        this.rulesSchema.variables &&
        new RuleEngine(this.rulesSchema).isValidRuleSet(
          this.$store.state.config.rules,
        )
      );
    },
  },
  mounted() {
    if (this.isOrgAdmin) {
      this.getConfigChecks();
    }
  },
  methods: {
    ...mapActions(['getConfigChecks', 'deleteCheck', 'saveCheck']),
    ...mapGetters({ getChecks: 'getChecks' }),
    ...mapMutations(['setChecks', 'setCurrentEditingItem']),
    getCheckSubtitle(check) {
      return `${
        check.triggers.length > 0
          ? check.triggers.length === 1
            ? '1 document filter'
            : `${check.triggers.length} document filters`
          : 'All documents'
      } • ${
        check.requirements.length > 0
          ? check.requirements.length === 1
            ? '1 requirement'
            : `${check.requirements.length} requirements`
          : 'No requirements'
      } • ${
        check.public && check.required_for_collect
          ? 'Show to users • Required for collect'
          : check.public
            ? 'Show to users'
            : 'Hide from users'
      }`;
    },
    onDeleteClick(data) {
      this.checkToBeDeleted = data.item;
    },
    addNewCheck() {
      openModal('new-checks-modal');
    },
    onCheckNameChange(value) {
      this.newCheckName = value;
    },
    createCheck() {
      const newCheck = {
        name: this.newCheckName.trim(),
        isDraft: true,
        enabled: true,
        public: false,
        required_for_collect: false,
        requirements: [
          {
            name: null,
            operator: null,
            value: null,
          },
        ],
        triggers: [],
      };
      this.setChecks([...this.getChecks(), newCheck]);
      this.setCurrentEditingItem(newCheck);
      this.$router.push({
        name: 'decision-engine-checks-edit',
      });
    },

    onEditClick(params) {
      this.$emit('hide-tooltips');
      this.setCurrentEditingItem(params.item);
      this.$router.push({
        name: 'decision-engine-checks-edit',
      });
    },
  },
};
</script>

<template>
  <div class="checks-page">
    <div class="content d-flex flex-column">
      <div
        class="decision-engine-page-header d-flex justify-content-between align-items-center"
      >
        <div class="text flex-grow-1 i-text-heading-5 i-text-neutral-120">
          Document Checks
        </div>
        <div v-if="checks.length > 0" class="flex-shrink-0">
          <i-button
            v-if="isOrgAdmin"
            text="New Check"
            data-testid="new-check-button"
            :size="iButtonSizes.MEDIUM"
            @button-click="addNewCheck()"
          ></i-button>
        </div>
      </div>

      <!-- page content -->
      <div class="decision-engine-page-content d-flex flex-column i-gap-11">
        <!-- item description -->
        <decision-engine-item-description
          title="Document Checks"
          description="Checks are sets of requirements that can automatically be applied to your documents."
        />

        <!-- empty page -->
        <empty-page
          v-if="checks.length === 0 && !checksLoading"
          class="d-flex flex-grow-1"
          icon="large-file-search"
          title="Start by creating a new document check"
          main-button-data-test-id="new-check-button"
          subtitle="All of your document checks will appear here."
          :button-text="isOrgAdmin ? 'New Check' : null"
          @on-button-click="addNewCheck"
        ></empty-page>

        <!-- loading screen-->
        <div
          v-if="checksLoading"
          class="d-flex i-my-5 w-100 align-items-center justify-content-center"
        >
          <i-spinner></i-spinner>
        </div>

        <!-- list -->
        <div
          v-if="checks.length > 0 && !checksLoading"
          class="checks-list d-flex flex-column align-items-start i-radius-2xl"
        >
          <decision-engine-card
            v-for="(check, checkIndex) in checks"
            :key="checkIndex"
            :title="check.name"
            :subtitle="getCheckSubtitle(check)"
            :item="check"
            tooltip-toggle-on="Disable Check"
            tooltip-toggle-off="Enable Check"
            tooltip-edit="Edit Check"
            tooltip-delete="Delete Check"
            data-testid="check-card"
            @delete-click="onDeleteClick"
            @edit-click="onEditClick"
            @card-click="onEditClick"
          ></decision-engine-card>
        </div>
      </div>
    </div>

    <!-- new Check Modal -->
    <i-modal
      modal-id="new-checks-modal"
      data-testid="new-checks-modal"
      title="New Document Check"
      :confirm-button-disabled="!validCheckName"
      confirm-button-text="Continue"
      :submit-on-enter="true"
      :input-focus-on-open="true"
      @on-submit="createCheck"
    >
      <template #body-content>
        <i-text-input
          ref="newCheckInput"
          :model-value="newCheckName"
          placeholder="Check name"
          :show-hint-text="true"
          hint-text="For example: 'Bank Statement Validity' or 'KYB Requirements'"
          data-testid="new-checks-modal-input"
          @update:model-value="onCheckNameChange"
        ></i-text-input>
      </template>
    </i-modal>

    <!-- check delete -->
    <check-delete
      :check-to-be-deleted="checkToBeDeleted"
      @on-close="checkToBeDeleted = null"
    ></check-delete>
  </div>
</template>

<style lang="scss" scoped>
.checks-page {
  height: 100%;

  .content {
    height: 100%;
  }

  .checks-list {
    $border-radius: 16px;

    border: 1px solid $light-neutral-60;

    .decision-engine-card {
      border-bottom: 1px solid $light-neutral-60;
      width: 100%;
    }

    .decision-engine-card:last-of-type {
      border-bottom: 0;
      border-bottom-left-radius: $border-radius;
      border-bottom-right-radius: $border-radius;
    }

    .decision-engine-card:first-of-type {
      border-top-left-radius: $border-radius;
      border-top-right-radius: $border-radius;
    }
  }
}

.subheading-wrap {
  max-width: 70%;
}
</style>
